import { useCallback, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface UseFocusTrapResult {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<Element | null>;
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const captureTrigger = (triggerRef: React.MutableRefObject<Element | null>): void => {
  triggerRef.current = document.activeElement;
};

const restoreFocus = (triggerRef: React.MutableRefObject<Element | null>): void => {
  if (triggerRef.current instanceof HTMLElement) {
    triggerRef.current.focus();
  }
  triggerRef.current = null;
};

interface FocusBoundary {
  first: HTMLElement;
  last: HTMLElement;
}

const getFocusBoundary = (wrapper: HTMLDivElement): FocusBoundary | null => {
  const focusable = wrapper.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  return first && last ? { first, last } : null;
};

const trapTabKey = (e: KeyboardEvent, boundary: FocusBoundary): void => {
  if (e.shiftKey && document.activeElement === boundary.first) {
    e.preventDefault();
    boundary.last.focus();
  } else if (!e.shiftKey && document.activeElement === boundary.last) {
    e.preventDefault();
    boundary.first.focus();
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useFocusTrap = (isOpen: boolean): UseFocusTrapResult => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      captureTrigger(triggerRef);
    } else {
      restoreFocus(triggerRef);
    }
  }, [isOpen]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !wrapperRef.current) return;
    const boundary = getFocusBoundary(wrapperRef.current);
    if (boundary) trapTabKey(e, boundary);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  return { wrapperRef, triggerRef };
};
